'use client';

import { createClient } from '@/utils/supabase/client';
import { getUser, getUserSubscriptions } from '@/utils/supabase/queries';
import { Plan } from '@/types/profile';
import { cx } from '@/utils/cx';
import { Transition } from '@headlessui/react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  CircleBackslashIcon,
  Cross1Icon,
  LightningBoltIcon,
  LockClosedIcon,
  PersonIcon,
} from '@radix-ui/react-icons';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import Link from 'next/link';
import React, { useState, useEffect } from 'react';

const ProfileTabDetails = () => {
  const supabase = createClient();
  const [user, setUser] = useState<object | null>(null);

  useEffect(() => {
    const getUserDetails = async () => {
      const user = await getUser(supabase);
      setUser(user);
    };
    getUserDetails();
  }, [supabase]);

  if (!user) {
    return null;
  }

  return (
    <div>
      <div className="text-2xl font-bold uppercase italic text-highlights-white">
        Profile
      </div>
      <div className="h-4" />
      <span>
        <span className="text-zinc-400">Email:</span> {(user as any).email}
      </span>
    </div>
  );
};

const SecurityTabDetails = () => {
  const supabase = createClient();
  const [user, setUser] = useState<object | null>(null);

  useEffect(() => {
    const getUserDetails = async () => {
      const user = await getUser(supabase);
      setUser(user);
    };
    getUserDetails();
  }, [supabase]);

  if (!user) {
    return null;
  }

  return (
    <div>
      <div className="text-2xl font-bold uppercase italic text-highlights-white">
        Security
      </div>
      <div className="h-4" />
      <Link
        href="/logout"
        className={cx(
          'inline-flex w-full select-none justify-center rounded-full px-6 py-2 text-lg font-bold uppercase italic leading-none',
          'bg-highlights-brand text-highlights-white hover:bg-wave-dark hover:text-highlights-white',
          'border border-transparent',
          'focus:outline-none focus-visible:bg-wave-dark focus-visible:text-highlights-white focus-visible:ring focus-visible:ring-wave-brown focus-visible:ring-opacity-75'
        )}
      >
        Logout
      </Link>
    </div>
  );
};

const SubscriptionTabDetails = () => {
  const [isProUser, setIsProUser] = useState<boolean | null>(null);

  useEffect(() => {
    const checkUserStatus = async () => {
      const supabase = createClient();
      const subscriptions = await getUserSubscriptions(supabase);
      setIsProUser(subscriptions.includes(Plan.PRO) || subscriptions.includes(Plan.LIFETIME));
    };
    checkUserStatus();
  }, []);

  return (
    <div>
      <div className="text-2xl font-bold uppercase italic text-highlights-white">
        Subscription
      </div>
      <div className="h-4" />
      {isProUser ? (
        <>
          <div>
            <span className="inline-flex flex-row items-center">
              <LightningBoltIcon className="mr-2 text-wave-brown" /> You are
              subscribed to Viddyoze Premium.
            </span>
          </div>
          {/* <div className="pt-2">
            <span className="italic text-gray-600">
              <a
                className="text-wave-brown underline"
                href="https://beatwavehelp.zendesk.com/hc/en-gb/requests/new"
              >
                Click here
              </a>{' '}
              to get in touch or cancel your subscription.
            </span>
          </div> */}
        </>
      ) : (
        <>
          <div>
            <span className="inline-flex flex-row items-center">
              <CircleBackslashIcon className="mr-2 text-wave-red" /> You have no
              subscriptions.
            </span>
          </div>
          <div className="mt-5">
            <a
              href={`https://viddyoze.thrivecart.com/beatwave/?passthrough[return_url]=${encodeURIComponent(
                window.location.href
              )}`}
              className={cx(
                'rounded-full bg-wave-brown px-5 py-2 text-lg text-slate-800',
                'font-bold uppercase italic'
              )}
            >
              Go premium
            </a>
          </div>
        </>
      )}
    </div>
  );
};

const tabs = [
  { title: 'Profile', icon: <PersonIcon />, value: 'profile' },
  { title: 'Subscription', icon: <LightningBoltIcon />, value: 'subscription' },
  { title: 'Security', icon: <LockClosedIcon />, value: 'security' },
] as const;

const [defaultTab] = tabs;

const AccountSettingsTabs = () => {
  return (
    <TabsPrimitive.Root defaultValue={defaultTab.value}>
      <div className="flex flex-col md:flex-row">
        <TabsPrimitive.List
          className={cx('flex w-full md:mr-8 md:mt-3 md:w-36 md:flex-col')}
        >
          {tabs.map(({ title, icon, value }) => (
            <TabsPrimitive.Trigger
              key={`tab-trigger-${value}`}
              value={value}
              className={cx(
                'group',
                'rounded-full',
                'radix-state-active:bg-wave-brown radix-state-active:bg-opacity-20 radix-state-inactive:bg-transparent',
                'radix-state-active:text-wave-brown radix-state-inactive:text-highlights-white',
                'flex-1 px-3 py-2.5',
                'focus:z-10 focus:outline-none focus-visible:ring focus-visible:ring-wave-brown focus-visible:ring-opacity-75',
                'md:flex-initial',
                'md:text-left'
              )}
            >
              <span
                className={cx(
                  'inline-flex flex-row items-center',
                  'text-sm font-medium'
                )}
              >
                <span className="-mt-0.5 mr-2">{icon}</span>
                <span>{title}</span>
              </span>
            </TabsPrimitive.Trigger>
          ))}
        </TabsPrimitive.List>
        <div className={cx('max-w-screen-sm py-4')}>
          {tabs.map(({ value }) => (
            <TabsPrimitive.Content key={`tab-content-${value}`} value={value}>
              {value === 'profile' && <ProfileTabDetails />}
              {value === 'subscription' && <SubscriptionTabDetails />}
              {value === 'security' && <SecurityTabDetails />}
            </TabsPrimitive.Content>
          ))}
        </div>
      </div>
    </TabsPrimitive.Root>
  );
};

export const AccountSettingsDialog = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
      <DialogPrimitive.Trigger asChild>
        <div>{children}</div>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal forceMount>
        <Transition.Root show={isOpen}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogPrimitive.Overlay
              forceMount
              className="fixed inset-0 z-20 bg-black/50"
            />
          </Transition.Child>

          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPrimitive.Content
              forceMount
              aria-describedby="account-settings-dialog-description"
              className={cx(
                'fixed z-50',
                'w-[95vw] max-w-xl rounded-2xl p-6 md:w-full',
                'min-h-[20rem]',
                'left-[50%] top-[50%] -translate-x-[50%] -translate-y-[50%]',
                'bg-zinc-800',
                'focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75'
              )}
            >
              <DialogPrimitive.Title className="sr-only">
                Account Settings
              </DialogPrimitive.Title>
              <div className="space-y-4">
                <div className="itemx-center flex flex-row justify-between">
                  <div className="flex flex-1 flex-row items-center">
                    <div className="mr-3 h-9 w-9 rounded-full bg-neutral-500" />
                    <div className="text-2xl font-bold italic text-wave-brown">
                      Account
                    </div>
                  </div>
                  <DialogPrimitive.Close
                    className={cx(
                      'inline-flex h-8 w-8 items-center justify-center rounded-full p-1',
                      'focus:outline-none focus-visible:ring focus-visible:ring-wave-brown focus-visible:ring-opacity-75'
                    )}
                  >
                    <Cross1Icon className="h-4 w-4 text-zinc-500 hover:text-zinc-400" />
                  </DialogPrimitive.Close>
                </div>
                <AccountSettingsTabs />
              </div>
            </DialogPrimitive.Content>
          </Transition.Child>
        </Transition.Root>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};